import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/common/SEO";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import RelatedGuides from "../../components/common/RelatedGuides";
import { dailyBudgetData, priceData, faqData, relatedGuides } from "./data";

const BudgetGuide = () => {
  // State for the opened FAQ item – no TS type annotation needed for plain JS/TSX
  const [openFaq, setOpenFaq] = useState(null);

  // Toggle FAQ item
  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    {
      label: "Travel Guide",
      path: "/antigua-guatemala-travel-guide",
    },
    {
      label: "Budget Guide",
      path: "/antigua-guatemala-budget-guide",
    },
  ];

  // JSON‑LD schema – wrapped in a try / catch to avoid runtime errors if data is malformed
  let jsonLd = {};
  try {
    jsonLd = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline:
            "Antigua Guatemala Budget Guide: Complete Cost Breakdown",
          description:
            "Comprehensive budget guide for Antigua Guatemala with daily costs, accommodation prices, food expenses, activity costs, and money-saving tips for every travel style.",
          image:
            "https://public.readdy.ai/ai/img_res/fddca7e82fed22d3b8cbb4c5f1ff26f6.jpg",
          author: {
            "@type": "Organization",
            name: "Antigua Guatemala Travel Guide",
          },
          publisher: {
            "@type": "Organization",
            name: "Antigua Guatemala Travel Guide",
            logo: {
              "@type": "ImageObject",
              url: "https://la-antigua-guatemala.com/logo.png",
            },
          },
          datePublished: "2025-01-15",
          dateModified: "2025-01-15",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://la-antigua-guatemala.com/antigua-guatemala-budget-guide",
          },
          inLanguage: "en-US",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://la-antigua-guatemala.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Travel Guide",
              item: "https://la-antigua-guatemala.com/antigua-guatemala-travel-guide",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Budget Guide",
              item: "https://la-antigua-guatemala.com/antigua-guatemala-budget-guide",
            },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqData.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        },
      ],
    };
  } catch (e) {
    console.error("Failed to build JSON‑LD schema:", e);
  }

  return (
    <>
      <SEO
        title="Antigua Guatemala Budget: Complete Cost Guide (2026)"
        description="How much does Antigua Guatemala cost? Complete budget breakdown with daily costs, accommodation prices, food expenses, and money-saving tips for every travel style."
        canonical="https://la-antigua-guatemala.com/antigua-guatemala-budget-guide"
        ogImage={{
          url: "https://readdy.ai/api/search-image?query=antigua%20guatemala%20colorful%20market%20with%20local%20vendors%20selling%20textiles%20and%20traditional%20goods%20with%20colonial%20architecture%20background&width=1200&height=630&seq=budgethero1&orientation=landscape",
          width: 1200,
          height: 630
        }}
        schema={jsonLd}
      />

      <Navbar darkText />

      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative h-[500px] w-full overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=antigua%20guatemala%20colorful%20market%20with%20local%20vendors%20selling%20textiles%20and%20traditional%20goods%20with%20colonial%20architecture%20background%20simple%20clean&width=1920&height=800&seq=budgethero2&orientation=landscape"
            alt="Antigua Guatemala market with local vendors and colorful textiles"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white px-6 max-w-4xl">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Antigua Guatemala Budget Guide
              </h1>
              <p className="text-xl md:text-2xl font-light">
                Complete cost breakdown for every travel style
              </p>
            </div>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto px-6 py-16">
          {/* Quick Take Box */}
          <div className="bg-teal-50 border-l-4 border-teal-500 p-8 mb-12 rounded-r-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Take</h2>
            <div className="space-y-3 text-gray-800">
              <p>
                <strong>Backpacker Budget:</strong> $35-85/day (hostels,
                street food, budget tours)
              </p>
              <p>
                <strong>Mid-Range Budget:</strong> $105-210/day (comfortable
                hotels, mix of restaurants, standard tours)
              </p>
              <p>
                <strong>Comfort Budget:</strong> $240-440/day (boutique
                hotels, upscale dining, premium experiences)
              </p>
              <p>
                <strong>3-Day Trip:</strong> $105-255 (budget) | $315-630
                (mid-range) | $720-1,320 (comfort)
              </p>
              <p>
                <strong>2-Week Trip:</strong> $490-1,190 (budget) | $1,470-2,940
                (mid-range) | $3,360-6,160 (comfort)
              </p>
            </div>
          </div>

          {/* Quick Answer */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Is Antigua Guatemala Expensive?
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              <strong>Antigua Guatemala is moderately priced</strong> — more
              expensive than rural Guatemala but significantly cheaper than
              Costa Rica, Belize, or US cities. Budget backpackers can
              comfortably travel on $35-85 per day staying in hostels, eating
              street food, and taking budget tours. Mid-range travelers
              spending $105-210 daily enjoy comfortable hotels, varied dining,
              and quality tours. Comfort travelers at $240-440 per day
              experience boutique hotels, upscale restaurants, and premium
              experiences.
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              The beauty of Antigua is its range. You can eat a delicious
              street taco for $2 or dine at an upscale restaurant for $35.
              Sleep in a $12 hostel dorm or a $250 boutique hotel. Hike
              Acatenango for $50 or book a premium tour for $120. Unlike some
              destinations that force a certain spending level, Antigua
              accommodates every budget. Your costs depend entirely on your
              choices, travel style, and season (rainy season offers 40-60%
              discounts).
            </p>
          </div>

          {/* Daily Budget Breakdown */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Daily Budget Breakdown by Travel Style
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Here's what you can expect to spend per day in Antigua Guatemala
              based on three common travel styles. These estimates include
              accommodation, food, activities, and local transport.
            </p>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-12">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Traveler Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Accommodation
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Food
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Activities
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Transport
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Daily Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {dailyBudgetData.map((row, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {row.type}
                        </td>
                        <td className="px-6 py-4 text-gray-700">
                          {row.accommodation}
                        </td>
                        <td className="px-6 py-4 text-gray-700">{row.food}</td>
                        <td className="px-6 py-4 text-gray-700">
                          {row.activities}
                        </td>
                        <td className="px-6 py-4 text-gray-700">
                          {row.transport}
                        </td>
                        <td className="px-6 py-4 font-bold text-teal-600">
                          {row.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Backpacker Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-wallet-line text-2xl text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Backpacker
                  </h3>
                </div>
                <p className="text-3xl font-bold text-blue-600 mb-4">
                  $35-85/day
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mr-2 mt-0.5" />
                    <span>Hostel dorms or budget hotels</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mr-2 mt-0.5" />
                    <span>Street food and local comedores</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mr-2 mt-0.5" />
                    <span>Free walking tours, budget hikes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mr-2 mt-0.5" />
                    <span>Tuk‑tuks and shared shuttles</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-600 mr-2 mt-0.5" />
                    <span>Self‑guided exploration</span>
                  </li>
                </ul>
              </div>

              {/* Mid‑Range Card */}
              <div className="bg-white border border-teal-200 rounded-xl p-8 shadow-md border-2">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-star-line text-2xl text-teal-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Mid‑Range
                  </h3>
                </div>
                <p className="text-3xl font-bold text-teal-600 mb-4">
                  $105-210/day
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-0.5" />
                    <span>Comfortable hotels or guesthouses</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-0.5" />
                    <span>Mix of local and tourist restaurants</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-0.5" />
                    <span>Standard tours and activities</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-0.5" />
                    <span>Occasional taxis or private transport</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-600 mr-2 mt-0.5" />
                    <span>Balance of comfort and value</span>
                  </li>
                </ul>
              </div>

              {/* Comfort Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-vip-crown-line text-2xl text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Comfort
                  </h3>
                </div>
                <p className="text-3xl font-bold text-amber-600 mb-4">
                  $240-440/day
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-amber-600 mr-2 mt-0.5" />
                    <span>Boutique hotels and luxury stays</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-amber-600 mr-2 mt-0.5" />
                    <span>Upscale restaurants and cafes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-amber-600 mr-2 mt-0.5" />
                    <span>Premium tours and private guides</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-amber-600 mr-2 mt-0.5" />
                    <span>Private transfers and taxis</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-amber-600 mr-2 mt-0.5" />
                    <span>Maximum comfort and convenience</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Pro Tip 1 */}
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-12 rounded-r-lg">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1" />
              <div>
                <h3 className="font-bold text-gray-900 mb-2">
                  Pro Tip: Rainy Season Savings
                </h3>
                <p className="text-gray-800">
                  Visit during rainy season (May‑October) and save 40‑60% on
                  accommodation and tours. Mornings are usually sunny for
                  sightseeing, with rain falling predictably in afternoons.
                  A mid‑range hotel that costs $100/night in February drops to
                  $40‑60 in June. If you're flexible with weather, rainy season
                  offers incredible value.
                </p>
              </div>
            </div>
          </div>

          {/* Detailed Price Table */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Detailed Price Breakdown
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Here are realistic prices for common expenses in Antigua Guatemala.
              Prices vary by season (high season = January‑March, rainy season =
              May‑October) and location within the city.
            </p>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Item
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Budget
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Mid‑Range
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">
                        Comfort
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {priceData.map((row, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-6 py-4 font-semibold text-gray-900">
                          {row.item}
                        </td>
                        <td className="px-6 py-4 text-gray-700">{row.budget}</td>
                        <td className="px-6 py-4 text-gray-700">{row.mid}</td>
                        <td className="px-6 py-4 text-gray-700">{row.comfort}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Mid‑Page CTA */}
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-12 text-center mb-16 shadow-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Plan Your Antigua Trip?
            </h2>
            <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
              Let us create a personalized itinerary that matches your budget and
              travel style
            </p>
            <Link
              to="/plan-my-antigua-trip"
              className="inline-block bg-white text-teal-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-teal-50 transition-colors shadow-lg whitespace-nowrap"
            >
              Plan My Trip
            </Link>
          </div>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-bold text-gray-900 pr-8">
                      {faq.q}
                    </h3>
                    <i
                      className={`text-2xl text-teal-600 transition-transform ${
                        openFaq === index
                          ? "ri-arrow-up-s-line"
                          : "ri-arrow-down-s-line"
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-8 pb-6">
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Related Guides */}
          <RelatedGuides guides={relatedGuides} />

          {/* Final CTA */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-12 text-center shadow-lg">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get Your Free Antigua Itinerary PDF
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Download our comprehensive guide with day‑by‑day plans, budget
              breakdowns, and insider tips
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/plan-my-antigua-trip"
                className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
              >
                Download Free Itinerary
              </Link>
              <Link
                to="/plan-my-antigua-trip"
                className="inline-block bg-white text-gray-900 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
              >
                Plan My Trip
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </>
  );
};

export default BudgetGuide;
